import { AfterViewInit, Component, DestroyRef, Inject, inject, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { VersioningService } from '@client-monorepo/versioning';
import { FeedbackSheetComponent } from './components/feedback-sheet/feedback-sheet.component';
import { feedBackParamsModel, OpenFeedbackInterface } from './data-access/models/openFeedback.interface';
import { ApiImageService } from '@digipay/ng-ui-api-image';
import { debounceTime, defer, Observable, of, timer, zip } from 'rxjs';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { AbTestService, EmitterService, EmittingDataEnum, LayoutService, StorageService } from '@client-monorepo/common/utilities';
import { Title } from '@angular/platform-browser';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { NgxHybridService } from '@digipay/ngx-hybrid-service';
import { IdleTrackerService } from '@client-monorepo/common/user';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationService } from '@client-monorepo/common/location-management';
import { EventManagementApiService } from '@client-monorepo/common/event-management';

declare const window: OpenFeedbackInterface | any;

@Component({
  standalone: true,
  imports: [RouterModule, CommonModule],
  selector: 'dpx-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit, OnDestroy {
  title = 'dpx';
  private versioning = inject(VersioningService);
  bottomSheetService = inject(NgxBottomSheetService);
  apiImageService = inject(ApiImageService);
  router = inject(Router);
  private readonly regex = /[^?]*/;
  private titleService = inject(Title);
  layoutService = inject(LayoutService);
  eventTracker = inject(NgxEventTrackerService);
  private ngxHybridService = inject(NgxHybridService);
  private idleTrackerService = inject(IdleTrackerService);
  private storageService = inject(StorageService);
  private emitterService = inject(EmitterService);
  untilDestroy = inject(DestroyRef);
  private locationService = inject(LocationService);
  private eventManagementApiService = inject(EventManagementApiService);
  isResizing = signal(false);
  private isGettingLocation = signal(false);
  private checkHybridVersionInterval: ReturnType<typeof setInterval>;
  private cleanupVisibilityTracker: () => void = () => {};

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {
    this.versioning.run();
    this.eventTracker.init();
    this.apiImageService.prefixUrl = this.environment[AbTestService.callApiWithAbsoluteUrl() ? 'base_url_origin_abs' : 'base_url_origin'];
  }

  ngAfterViewInit(): void {
    this.monkeyPatchWindowOpen();
    this.handleAppInitialization();
    this.eventTracker.initIntrackOnSiteMessaging();
    this.eventTracker.initIntrackWebPush();
  }

  monkeyPatchWindowOpen(): void {
    const originalWindowOpen = window.open;
    const timestamp = Date.now();

    const patchedWindowOpen = (url?: string | URL, target?: string, features?: string): Window | null => {
      const urlStr = typeof url === 'string' ? url : (url?.toString() ?? '');

      const shouldSetRedirectionTimestamp =
        urlStr &&
        this.idleTrackerService.validPathOnSetRedirectionTimestamp() &&
        (this.ngxHybridService.isAndroidHybrid() || target === '_blank');

      if (shouldSetRedirectionTimestamp) {
        this.storageService.setRedirectionTimestamp(timestamp);
      }

      return originalWindowOpen.call(window, urlStr, target, features || '');
    };

    window.open = patchedWindowOpen.bind(this);
  }

  private initVisibilityTracker(): Observable<void> {
    return defer(() => {
      const isLoggedIn = this.storageService.isLoggedIn();
      const { cleanup, visibilityCallbackInvoked } = this.idleTrackerService.initVisibilityTracker(() => {});
      this.storageService.removeRedirectionTimestamp();
      this.cleanupVisibilityTracker = cleanup;

      if (!visibilityCallbackInvoked || !isLoggedIn) {
        return of(undefined);
      }

      this.storageService.expireToken();
      return of(undefined);
      // TODO: Use this part after adding token expiration by backend
      // return this.tacService.getTac().pipe(
      //   catchError(() => of(null)),
      //   map(() => undefined),
      // );
    });
  }

  private handleAppInitialization(): void {
    zip([timer(3000), this.initVisibilityTracker()]).subscribe({
      next: () => this.finalizeAppInit(),
      error: () => this.finalizeAppInit(),
    });
  }

  private finalizeAppInit(): void {
    document.getElementById('splash_bg')?.remove();
    document.getElementById('nativeSplash')?.remove();
    this.subscribeToHorizontalResize();
    this.titleServiceRouterEvent();
    this.observeAppState();
    this.afterLoginInitialize();
    this.openFeedBackModal();
  }

  afterLoginInitialize() {
    if (this.storageService.isLoggedIn()) {
      this.sendVisitEvent();
      this.getUserLocation();
      this.checkHybridVersionAvailable();
    }
    this.emitterService.sharedData.pipe(takeUntilDestroyed(this.untilDestroy)).subscribe({
      next: (event) => {
        if (event === EmittingDataEnum.USER_HAS_LOGGED_IN) {
          this.sendVisitEvent();
          this.getUserLocation();
          this.checkHybridVersionAvailable();
        }
      },
    });
  }

  private getUserLocation() {
    if (!AbTestService.showLocationSheet()) return;
    if (this.locationService.checkLocationCollection()) {
      this.isGettingLocation.set(true);
      this.locationService.getLocation(false, 3600000).subscribe({
        next: (res) => {
          if (res) {
            this.sendLocation(res.latitude, res.longitude);
          }
          this.isGettingLocation.set(false);
        },
        error: () => {
          this.isGettingLocation.set(false);
        },
      });
    }
  }

  private checkHybridVersionAvailable(): void {
    if (!this.ngxHybridService.isAndroidHybrid()) return;
    this.checkHybridVersionInterval = setInterval(() => {
      if (this.isGettingLocation() || this.versioning.isUpdating() || window.isGettingPassword) return;
      this.versioning.checkHybridVersion();
      clearInterval(this.checkHybridVersionInterval);
    }, 5000);
  }

  private sendLocation(latitude: number, longitude: number): void {
    this.eventManagementApiService.sendLocationData(this.storageService.getUserId() || '', latitude, longitude).subscribe();
    this.storageService.setLocationEventTimeStamp(Date.now());
  }

  private observeAppState(): void {
    if (!this.ngxHybridService.isAndroidHybrid()) return;
    this.idleTrackerService.observeAppStateChange();
  }

  subscribeToHorizontalResize(): void {
    this.layoutService
      .onHorizontalResize()
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.isResizing.set(true);
        setTimeout(() => {
          this.isResizing.set(false);
        }, 10);
      });
  }

  private openFeedBackModal(): void {
    window.openFeedback = (params: feedBackParamsModel) => {
      this.bottomSheetService.openBottomSheet(FeedbackSheetComponent, { feedBackData: params }, { height: '70%' });
    };
  }
  private titleServiceRouterEvent() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.titleService.setTitle('دیجی‌پی | خدمات مالی و پرداخت'); // Reset the title after navigation
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupVisibilityTracker(); // Remove listener
    if (this.checkHybridVersionInterval) {
      clearInterval(this.checkHybridVersionInterval);
    }
  }

  sendVisitEvent(): void {
    this.eventManagementApiService.sendVisitData(this.storageService.getUserId() || '').subscribe();
  }
}
